// Comprehensive Spam Word Detection System
// Free, no API required - uses built-in spam word database
// Enhanced for trucking/logistics industry - reduced false positives

// Words that are safe in trucking/logistics context and should NOT trigger spam
export const TRUCKING_SAFE_WORDS = new Set([
  'load', 'loads', 'freight', 'carrier', 'broker', 'dispatch',
  'fleet', 'truck', 'trucking', 'trailer', 'dot', 'mc', 'fmcsa',
  'lane', 'lanes', 'route', 'routes', 'capacity', 'dedicated',
  'ltl', 'ftl', 'drayage', 'intermodal', 'reefer', 'flatbed',
  'dry van', 'hazmat', 'oversize', 'owner operator', 'partnership',
  'rates', 'rate', 'shipment', 'shipping', 'delivery', 'pickup',
  'bol', 'pod', 'eld', 'compliance', 'safety', 'insurance',
  'authority', 'operating authority', 'cdl', 'driver', 'drivers',
  'dispatch services', 'factoring', 'fuel card',
]);

export const SPAM_WORDS = {
  // High-risk spam triggers
  high: [
    'free', 'winner', 'click here', 'click now', 'act now', 'limited time',
    'urgent', 'congratulations', 'guaranteed', '100%', 'money back',
    'no obligation', 'risk free', 'bonus', 'cash', 'credit card',
    'mortgage', 'refinance', 'loan', 'debt', 'consolidate',
    'viagra', 'pharmacy', 'pills', 'weight loss', 'lose weight',
    'work from home', 'make money', 'earn money', 'get paid',
    'million dollars', 'billion', 'inherit', 'beneficiary',
    'lottery', 'prize', 'sweepstakes', 'gift certificate',
    'buy now', 'order now', 'call now', 'subscribe', 'unsubscribe here',
    'increase sales', 'increase traffic', 'mlm', 'multi-level',
    'nigerian', 'prince', 'inheritance', 'bank account',
    'social security', 'tax refund', 'irs', 'paypal', 'western union',
    'wire transfer', 'bitcoin', 'cryptocurrency', 'investment opportunity',
  ],
  
  // Medium-risk words
  medium: [
    'special', 'offer', 'deal', 'discount', 'save', 'sale',
    'limited', 'only', 'today', 'now', 'hurry', 'fast',
    'instant', 'immediately', 'asap', 'urgent action',
    'important', 'attention', 'notice', 'alert', 'warning',
    'verify', 'confirm', 'update', 'suspended', 'expired',
    'trial', 'demo', 'sample', 'free trial', 'no cost',
    'amazing', 'incredible', 'unbelievable', 'revolutionary',
    'breakthrough', 'miracle', 'secret', 'hidden',
    'exclusive', 'members only', 'vip', 'elite', 'premium',
    'best', 'lowest price', 'cheap', 'affordable', 'budget',
  ],
  
  // Low-risk but should be cautious
  low: [
    'hello', 'hi there', 'dear friend', 'dear sir/madam',
    'opportunity', 'business', 'partnership', 'investment',
    'guarantee', 'promise', 'certified', 'approved',
    'official', 'legal', 'compliance', 'secure',
    'protected', 'safe', 'trusted', 'verified',
    'info', 'information', 'details', 'read more',
    'learn more', 'find out', 'discover', 'explore',
  ],
};

export const SPAM_PATTERNS = [
  // Excessive punctuation
  { pattern: /!{2,}/g, message: 'Multiple exclamation marks (!!!)' },
  { pattern: /\?{2,}/g, message: 'Multiple question marks (???)' },
  
  // All caps words
  { pattern: /\b[A-Z]{4,}\b/g, message: 'ALL CAPS words' },
  
  // Money symbols excessive
  { pattern: /\${2,}/g, message: 'Multiple dollar signs' },
  { pattern: /£{2,}/g, message: 'Multiple pound signs' },
  
  // Excessive spaces
  { pattern: /\s{3,}/g, message: 'Excessive spacing' },
  
  // HTML/Script attempts
  { pattern: /<script/gi, message: 'Script tags detected' },
  { pattern: /<iframe/gi, message: 'iFrame tags detected' },
  
  // Suspicious links
  { pattern: /bit\.ly|tinyurl|goo\.gl/gi, message: 'URL shorteners (suspicious)' },
  
  // Excessive links
  { pattern: /(https?:\/\/[^\s]+)/g, message: 'Multiple links', count: true },
];

export function analyzeSpamScore(text, subject = '') {
  const fullText = `${subject} ${text}`.toLowerCase();
  let score = 0;
  let warnings = [];
  let highlightWords = [];
  
  // Check high-risk words (5 points each) - skip trucking-safe words
  SPAM_WORDS.high.forEach(word => {
    if (TRUCKING_SAFE_WORDS.has(word.toLowerCase())) return;
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    const matches = fullText.match(regex);
    if (matches) {
      score += matches.length * 5;
      warnings.push(`"${word}" (High Risk) - found ${matches.length}x`);
      highlightWords.push({ word, risk: 'high', count: matches.length });
    }
  });
  
  // Check medium-risk words (2 points each) - skip trucking-safe words
  SPAM_WORDS.medium.forEach(word => {
    if (TRUCKING_SAFE_WORDS.has(word.toLowerCase())) return;
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    const matches = fullText.match(regex);
    if (matches) {
      score += matches.length * 2;
      warnings.push(`"${word}" (Medium Risk) - found ${matches.length}x`);
      highlightWords.push({ word, risk: 'medium', count: matches.length });
    }
  });
  
  // Check low-risk words (1 point each) - skip trucking-safe words
  SPAM_WORDS.low.forEach(word => {
    if (TRUCKING_SAFE_WORDS.has(word.toLowerCase())) return;
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    const matches = fullText.match(regex);
    if (matches) {
      score += matches.length * 1;
      if (matches.length > 2) {
        warnings.push(`"${word}" (Overused) - found ${matches.length}x`);
        highlightWords.push({ word, risk: 'low', count: matches.length });
      }
    }
  });
  
  // Check patterns
  SPAM_PATTERNS.forEach(({ pattern, message, count }) => {
    const matches = fullText.match(pattern);
    if (matches) {
      if (count && matches.length > 5) {
        score += matches.length * 2;
        warnings.push(`${message}: ${matches.length} found`);
      } else if (!count) {
        score += 3;
        warnings.push(message);
      }
    }
  });
  
  // Subject-specific checks
  if (subject) {
    if (subject.length < 10) {
      score += 2;
      warnings.push('Subject too short (<10 chars)');
    }
    if (subject.length > 100) {
      score += 3;
      warnings.push('Subject too long (>100 chars)');
    }
    if (subject === subject.toUpperCase() && subject.length > 5) {
      score += 5;
      warnings.push('Subject is ALL CAPS');
    }
  }
  
  // Body-specific checks
  if (text) {
    const wordCount = text.split(/\s+/).length;
    if (wordCount < 20) {
      score += 2;
      warnings.push('Email too short (<20 words)');
    }
    
    const linkCount = (text.match(/https?:\/\/[^\s]+/g) || []).length;
    if (linkCount > 5) {
      score += linkCount * 2;
      warnings.push(`Too many links (${linkCount})`);
    }
  }
  
  // Calculate percentage (0-100)
  const percentage = Math.min(score * 2, 100);
  
  return {
    score: percentage,
    risk: percentage < 20 ? 'low' : percentage < 50 ? 'medium' : 'high',
    warnings: warnings.slice(0, 10), // Top 10 issues
    highlightWords: highlightWords.slice(0, 20), // Top 20 words to highlight
    recommendation: getRecommendation(percentage),
  };
}

function getRecommendation(score) {
  if (score < 20) {
    return {
      level: 'excellent',
      message: 'Excellent! Your email looks professional and spam-free.',
      color: 'success',
    };
  } else if (score < 50) {
    return {
      level: 'good',
      message: 'Good, but consider removing some flagged words to improve deliverability.',
      color: 'warning',
    };
  } else if (score < 80) {
    return {
      level: 'risky',
      message: 'High spam risk! Replace flagged words immediately.',
      color: 'error',
    };
  } else {
    return {
      level: 'danger',
      message: 'CRITICAL: This email will almost certainly go to spam. Rewrite completely.',
      color: 'error',
    };
  }
}

export function suggestReplacements(word) {
  const replacements = {
    // High-risk replacements
    'free': ['complimentary', 'no-cost', 'included', 'at no charge'],
    'click here': ['learn more', 'view details', 'see more', 'read further'],
    'click now': ['explore now', 'discover', 'find out more'],
    'act now': ['respond today', 'take action', 'consider this'],
    'urgent': ['time-sensitive', 'important', 'priority'],
    'guaranteed': ['assured', 'confident', 'reliable'],
    'winner': ['selected', 'chosen', 'qualified'],
    'congratulations': ['great news', 'you qualify', 'you\'re eligible'],
    'buy now': ['purchase today', 'order', 'get started'],
    'limited time': ['time-bound', 'for a limited period', 'available soon'],
    'risk free': ['no-obligation', 'try it out', 'test drive'],
    'money back': ['satisfaction guaranteed', 'refund available'],
    'work from home': ['remote opportunity', 'flexible location', 'remote position'],
    'make money': ['earn income', 'generate revenue', 'increase earnings'],
    'discount': ['reduced price', 'special pricing', 'promotional rate'],
    'save': ['reduce costs', 'economize', 'cut expenses'],
    'cheap': ['affordable', 'economical', 'budget-friendly', 'cost-effective'],
    'amazing': ['impressive', 'notable', 'exceptional'],
    'incredible': ['remarkable', 'outstanding', 'extraordinary'],
    'best': ['top-rated', 'highly-rated', 'leading', 'premier'],
    'miracle': ['effective', 'proven', 'tested'],
  };
  
  return replacements[word.toLowerCase()] || ['use professional language', 'rephrase'];
}

export function highlightSpamWords(html, spamWords) {
  let highlighted = html;
  
  spamWords.forEach(({ word, risk }) => {
    const color = risk === 'high' ? '#ff1744' : risk === 'medium' ? '#ff9800' : '#ffc107';
    const regex = new RegExp(`\\b(${word})\\b`, 'gi');
    highlighted = highlighted.replace(
      regex,
      `<mark style="background-color: ${color}; padding: 2px 4px; border-radius: 3px; font-weight: 600;">$1</mark>`
    );
  });
  
  return highlighted;
}
